import React, {useEffect, useState} from 'react'
import {ReadOnlyTextField} from 'components/Shared/ReadOnlyTextField'
import {fetchCar} from 'services/CarService'
import {fetchDriver} from 'services'
import {useErrorHandling} from 'context/errorHandling'
import {HTTP_404_ERROR} from 'Constants'
import {fetchContractor} from 'services/ContractorService'
import {ReadOnlyEmailFieldSet} from "components/Shared/ReadOnlyEmailFieldSet";
import {PrimaryButton} from "../../Shared/PrimaryButton";
import {useTranslation} from 'react-i18next'
import {
  ContractorVerificationRequestDialog
} from "../../Shared/Dialogs/ContractorVerificationRequestDialog/ContractorVerificationRequestDialog";
import {CustomWarningMessageText, InfoMessageText} from "../../Shared/ErrorMessage";
import moment from "moment/moment";

function CarDriverDetailsProvider({value, children}) {
  const {internal, external} = value || {}
  const [driver, setDriver] = useState(null)
  const [car, setCar] = useState(null)

  useEffect(() => {
    if (internal && internal.driverId) {
      fetchDriver(internal.driverId).then(setDriver)
    }
  }, [internal])

  useEffect(() => {
    if (internal && internal.carId) {
      fetchCar(internal.carId).then(setCar)
    }
  }, [internal])

  if (internal) {
    if (driver && car) {
      return children({
        carRegistrationNumber: car.registrationNumber,
        driverInfo: driver.firstName + ' ' + driver.lastName
      })
    }
    return null
  }
  if (external) {
    return children({
      carRegistrationNumber: external.carRegistrationNumber,
      driverInfo: external.driverInfo
    })
  }
  return children(null)
}

function CarAndDriverDetailsDisplay({value, previousValue}) {
  const {carRegistrationNumber, driverInfo} = value

  return (
    <>
      <ReadOnlyTextField
        label="order.details.carRegistrationNumber"
        value={carRegistrationNumber}
        previousValue={previousValue && {value: previousValue.carRegistrationNumber}}/>
      <ReadOnlyTextField
        label="order.details.driverInfo"
        value={driverInfo}
        previousValue={previousValue && {value: previousValue.driverInfo}}/>
    </>
  )
}

export function CarrierDetails({value, previousValue, onCarrierDetails, carrierVerificationWarnings}) {
  const {handleError} = useErrorHandling()
  const {t} = useTranslation()

  const getCarrierId = deliveryMethod => deliveryMethod && (deliveryMethod.internal
    ? deliveryMethod.internal.carrierId
    : deliveryMethod.external.carrierId)

  const getContactInfo = deliveryMethod => deliveryMethod && (
    deliveryMethod.internal ? deliveryMethod.internal.carrierContactInfo : deliveryMethod.external.carrierContactInfo
  )

  const carrierId = getCarrierId(value)

  const previousCarrierId = getCarrierId(previousValue)

  const [carrier, setCarrier] = useState(null)

  const [previousCarrier, setPreviousCarrier] = useState(null)
  const [verificationRequestDialogOpen, setVerificationRequestDialogOpen] = useState(false)
  const [hasVerificationWarnings, setHasVerificationWarnings] = useState(false)
  useEffect(() => {
    const warningsCopy = {...carrierVerificationWarnings, notAllData:false}
    setHasVerificationWarnings(Object.values(warningsCopy).some(item => item === true))
  }, [carrierVerificationWarnings, setHasVerificationWarnings])

  useEffect(() => {
    if (carrierId) {
      fetchContractor(carrierId)
        .then((carrier) => {
          setCarrier(carrier)
          if (carrier && onCarrierDetails) {
            onCarrierDetails(carrier)
          }

        })
        .catch((error) => handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.fetchCarrier'
        }))
    }
    if (previousCarrierId && (previousCarrierId !== carrierId)) {
      fetchContractor(previousCarrierId)
        .then(setPreviousCarrier)
        .catch((error) => handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.fetchCarrier'
        }))
    } else setPreviousCarrier(null)
  }, [carrierId, previousCarrierId, onCarrierDetails, handleError])

  function warningMessage() {
    if(carrierVerificationWarnings.hvExpirationDate){
      return t(`order.warnings.carrier.hvExpirationDate`)
    }
    if(carrierVerificationWarnings.carrierVerificationExpirationDate){
      return t(`order.warnings.carrier.carrierVerificationExpirationDate`)
    }
    if(carrierVerificationWarnings.noValidCarrierPolicies){
      return t(`order.warnings.carrier.noValidCarrierPolicies`)
    }
    if(carrierVerificationWarnings.carrierVerificationValidityAfterDischarge){
      return t(`order.warnings.carrier.carrierVerificationValidityAfterDischarge`)
    }
    return ''
  }

  function verificationMessage(value) {
    if (value.hvExpirationDate) {
      return `${t(
        'order.warnings.carrier.hvExpirationDateInfo', { date: `${moment(value.hvExpirationDate).format('DD-MM-YYYY')}` })}`
    }
    if (value.carrierVerificationExpirationDate) {
      return `${t(
        'order.warnings.carrier.carrierVerificationExpirationDateInfo', { date: `${moment(value.carrierVerificationExpirationDate).format('DD-MM-YYYY')}` })}`
    }
    return ''
  }

  return (
    <>
      <ReadOnlyTextField
        label="order.details.carrier"
        value={carrier ? carrier.fullName : ''}
        previousValue={previousCarrier && {value: previousCarrier.fullName}}/>
      {carrier && !hasVerificationWarnings && <InfoMessageText message={verificationMessage(carrier)}/>}
      {hasVerificationWarnings && <CustomWarningMessageText message={warningMessage()}/>}
      <PrimaryButton
        type="button"
        disabled={!carrier}
        label={t('contractorVerification.requestButton')}
        onClick={() => setVerificationRequestDialogOpen(true)}
        className={'in-form-control-button'}
      />
      <ReadOnlyEmailFieldSet
        label="order.details.carrierContactEmail"
        value={getContactInfo(value).emails}
        previousValue={previousValue && {value: getContactInfo(previousValue)?.emails}}/>
      <ReadOnlyTextField
        label="order.details.carrierContactInfo"
        value={getContactInfo(value).infoText}
        previousValue={previousValue && {value: getContactInfo(previousValue)?.infoText}}/>
      <CarDriverDetailsProvider value={previousValue}>{previousDetails =>
        <CarDriverDetailsProvider value={value}>{currentDetails =>
          <CarAndDriverDetailsDisplay value={currentDetails} previousValue={previousDetails}/>
        }
        </CarDriverDetailsProvider>}
      </CarDriverDetailsProvider>
      {carrier && <ContractorVerificationRequestDialog open={verificationRequestDialogOpen}
                                                       contractorId={carrier.id}
                                                       onClose={() => {
                                                         setVerificationRequestDialogOpen(false)
                                                       }}
      />}
    </>
  )
}
